import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import {Save, UserPlus, Phone} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"

const estadosBrasileiros = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG",
  "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"
]

const categoriasFeirante = [
  "Frutas",
  "Verduras e Legumes",
  "Queijos e Laticínios",
  "Carnes e Aves",
  "Peixes e Frutos do Mar",
  "Cama, Mesa e Banho",
  "Roupas e Confecções",
  "Calçados",
  "Artesanato",
  "Utensílios Domésticos",
  "Plantas e Flores",
  "Cereais e Grãos",
  "Temperos e Condimentos",
  "Doces e Guloseimas",
  "Lanches e Comidas Prontas",
  "Bebidas",
  "Brinquedos",
  "Eletrônicos e Acessórios",
  "Produtos de Limpeza",
  "Cosméticos e Perfumaria",
  "Aviamentos e Armarinhos",
  "Ferramentas",
  "Outros"
]

interface CadastrarFeiranteProps {
  editingFeiranteId?: string
  onSaveComplete?: () => void
}

export default function CadastrarFeirante({ editingFeiranteId, onSaveComplete }: CadastrarFeiranteProps) {
  const { addFeirante, updateFeirante, getFeiranteById } = useGuias()
  
  const [formData, setFormData] = useState({
    nomeCompleto: "",
    cpf: "",
    endereco: "",
    uf: "RN",
    contato: "",
    valorSemanal: "",
    enderecoLote: "",
    numeroLote: "",
    categorias: [] as string[]
  })

  useEffect(() => {
    if (editingFeiranteId) {
      const feirante = getFeiranteById(editingFeiranteId)
      if (feirante) {
        setFormData({
          nomeCompleto: feirante.nomeCompleto,
          cpf: feirante.cpf,
          endereco: feirante.endereco,
          uf: feirante.uf,
          contato: feirante.contato,
          valorSemanal: feirante.valorSemanal,
          enderecoLote: feirante.enderecoLote,
          numeroLote: feirante.numeroLote,
          categorias: feirante.categorias || []
        })
      }
    }
  }, [editingFeiranteId, getFeiranteById])

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, "")
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    }
    return value
  }

  const formatValor = (value: string) => {
    const numbers = value.replace(/\D/g, "")
    if (numbers === "") return "R$ 0,00"
    const numberValue = parseInt(numbers) / 100
    return "R$ " + numberValue.toFixed(2).replace(".", ",")
  }

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value)
    setFormData({ ...formData, cpf: formatted })
  }

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatValor(e.target.value)
    setFormData({ ...formData, valorSemanal: formatted })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (editingFeiranteId) {
      await updateFeirante(editingFeiranteId, formData)
    } else {
      await addFeirante(formData)
    }

    setFormData({
      nomeCompleto: "",
      cpf: "",
      endereco: "",
      uf: "RN",
      contato: "",
      valorSemanal: "",
      enderecoLote: "",
      numeroLote: "",
      categorias: []
    })

    if (onSaveComplete) {
      onSaveComplete()
    }
  }

  const abrirWhatsApp = () => {
    if (formData.contato) {
      const numero = formData.contato.replace(/\D/g, "")
      window.open(`https://wa.me/55${numero}`, "_blank")
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#004161] rounded-lg">
              <UserPlus className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {editingFeiranteId ? "Editar Feirante" : "Cadastrar Feirante"}
              </h2>
              <p className="text-sm text-gray-500">Preencha os dados do feirante</p>
            </div>
          </div>
          {formData.numeroLote && (
            <div className="bg-[#004161] text-white px-6 py-3 rounded-xl shadow-lg">
              <p className="text-xs font-medium opacity-90">LOTE Nº</p>
              <p className="text-2xl font-bold">{formData.numeroLote}</p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome Completo *
              </label>
              <input
                type="text"
                value={formData.nomeCompleto}
                onChange={(e) => setFormData({ ...formData, nomeCompleto: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CPF *
              </label>
              <input
                type="text"
                value={formData.cpf}
                onChange={handleCPFChange}
                placeholder="000.000.000-00"
                maxLength={14}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contato (WhatsApp) *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.contato}
                  onChange={(e) => setFormData({ ...formData, contato: e.target.value.replace(/\D/g, "") })}
                  placeholder="84999887766"
                  maxLength={11}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-12"
                  required
                />
                {formData.contato && (
                  <button
                    type="button"
                    onClick={abrirWhatsApp}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    title="Abrir WhatsApp"
                  >
                    <Phone className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Endereço *
              </label>
              <input
                type="text"
                value={formData.endereco}
                onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                UF (Estado) *
              </label>
              <select
                value={formData.uf}
                onChange={(e) => setFormData({ ...formData, uf: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              >
                {estadosBrasileiros.map((estado) => (
                  <option key={estado} value={estado}>
                    {estado}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valor Pago Semanalmente *
              </label>
              <input
                type="text"
                value={formData.valorSemanal}
                onChange={handleValorChange}
                placeholder="R$ 0,00"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Endereço do Lote *
              </label>
              <input
                type="text"
                value={formData.enderecoLote}
                onChange={(e) => setFormData({ ...formData, enderecoLote: e.target.value })}
                placeholder="Ex: Rua da Feira, Centro"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número do Lote *
              </label>
              <input
                type="text"
                value={formData.numeroLote}
                onChange={(e) => setFormData({ ...formData, numeroLote: e.target.value })}
                placeholder="Ex: 001, A-15, etc."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categorias (Selecione até 3) *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {categoriasFeirante.map((categoria) => (
                  <label
                    key={categoria}
                    className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.categorias.includes(categoria)
                        ? "border-[#004161] bg-[#004161] bg-opacity-10"
                        : "border-gray-300 hover:border-[#004161]"
                    } ${
                      formData.categorias.length >= 3 && !formData.categorias.includes(categoria)
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.categorias.includes(categoria)}
                      onChange={(e) => {
                        if (e.target.checked && formData.categorias.length < 3) {
                          setFormData({ ...formData, categorias: [...formData.categorias, categoria] })
                        } else if (!e.target.checked) {
                          setFormData({ ...formData, categorias: formData.categorias.filter(c => c !== categoria) })
                        }
                      }}
                      disabled={formData.categorias.length >= 3 && !formData.categorias.includes(categoria)}
                      className="w-4 h-4 text-[#004161] border-gray-300 rounded focus:ring-[#004161]"
                    />
                    <span className="text-sm font-medium text-gray-700">{categoria}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Selecionadas: {formData.categorias.length}/3
              </p>
            </div>
          </div>

          <div className="flex gap-4 pt-6 border-t">
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 bg-[#004161] text-white py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-shadow hover:bg-[#00567d]"
            >
              <Save className="w-5 h-5" />
              {editingFeiranteId ? "Atualizar Feirante" : "Cadastrar Feirante"}
            </motion.button>
          </div>
        </form>
      </div>
    </motion.div>
  )
}
